/**
 * metricLabels.js — Shared user-facing metric vocabulary.
 *
 * Goal: stop showing implementation mechanics (PRE, expected_distribution, P-Max,
 * entropy, fragility) in primary UI. Use friendly labels everywhere.
 * Technical names remain available in advanced diagnostics & tooltips.
 *
 * Used by: OverviewTab, PREAnalytics, HRLabReport, ReliabilitySummary,
 * ResultMethodNote, ReportHeader.
 */

// User-facing labels for technical metrics
export const METRIC_LABELS = {
  // Result distributions
  primary_result: 'Primary Result',
  expected_distribution: 'Primary Result',
  sampled_distribution: 'Sampled Outcomes',
  sampled_response: 'Simulated individual response',

  // Reliability
  confidence_score: 'Report Reliability',
  report_reliability: 'Report Reliability',
  method_quality: 'Method Quality',
  response_certainty: 'Response Certainty',
  segment_stability: 'Segment Stability',
  coverage: 'Data Coverage',

  // Distribution diversity
  normalized_entropy: 'Response Diversity',
  mean_probability_entropy: 'Individual Uncertainty',
  avg_p_max: 'Response Certainty',
  max_divergence: 'Max Segment Difference',
  fragility_index: 'Sensitivity',
  collapse_flag: 'Low Variation Warning',

  // Coverage
  coverage_rate: 'Coverage',
  is_full_census: 'Full census',

  // Mode
  pre_mode: 'Uncertainty-aware',
};

// Plain-English descriptions used in tooltips and help cards
export const METRIC_HELP = {
  primaryResult:
    'The official result for this simulation. Aggregated across all simulated individuals, accounting for response uncertainty.',
  sampledOutcomes:
    'The individual simulated answers selected from each probability distribution. Useful for row-level exports and reproducibility, but aggregate charts use the probability-weighted Primary Result.',
  reportReliability:
    'How much you can trust this report end-to-end. Combines method quality, response certainty, segment stability, and data coverage.',
  methodQuality:
    'Outcome mapping confidence, measurement setup, and how organizational context was applied.',
  responseCertainty:
    'On average, how confident the simulated individuals were in their top choice.',
  segmentStability:
    'How consistent the result is across demographic groups.',
  coverage:
    'How much of the intended population was processed.',
  responseDiversity:
    'How spread out responses are across available options. High = many options used; low = converged on one.',
  individualUncertainty:
    'How uncertain the simulated individuals were before a final answer was sampled.',
  sensitivity:
    'How likely results are to shift under small assumption changes.',
  maxSegmentDifference:
    'The largest gap between any demographic segment and the overall result.',
  lowVariationWarning:
    'Triggered when both response diversity and individual uncertainty are very low — interpret aggregate numbers cautiously.',
  uncertaintyAware:
    'This simulation accounts for uncertainty in individual responses. Aggregate charts use probability-weighted distributions instead of raw sampled counts.',
};

// Friendly label for context_mode
export const CONTEXT_MODE_LABELS = {
  standard: 'Standard',
  light: 'Light (background only)',
  none: 'No context',
};

export const CONTEXT_MODE_HELP = {
  standard:
    'Company context (documents, web claims, organizational background) was used as decision conditioning alongside individual profiles.',
  light:
    "Company context was passed as background only — employees' profiles drove the answer.",
  none:
    'Only the stimulus and individual profile were used; no organizational context was applied.',
};

// ---------------------------------------------------------------------------
// Primary Result selection — single source of truth for which distribution
// frontends should render in primary UI. Sampled is only for advanced.
// ---------------------------------------------------------------------------

/**
 * Picks the distribution to render in primary UI.
 * Rules:
 *   1. If expected (probability-weighted) data exists, use it. Mode = 'uncertainty_aware'.
 *   2. Otherwise fall back to sampled distribution. Mode = 'sampled'.
 *
 * @param {object} args
 * @param {object} [args.aggregation] - /api/simulations/{id}/aggregation payload
 * @param {object} [args.preData]     - /api/simulations/{id}/pre-analytics payload
 * @returns {{ distribution: object|null, percentages: object|null, mode: 'uncertainty_aware'|'sampled'|null, source: string|null }}
 */
export function getPrimaryDistribution({ aggregation = null, preData = null } = {}) {
  // Prefer aggregation-side expected (filter-aware) when available
  if (aggregation?.expected_percentages && Object.keys(aggregation.expected_percentages).length > 0) {
    return {
      distribution: aggregation.expected_distribution || null,
      percentages: aggregation.expected_percentages,
      mode: 'uncertainty_aware',
      source: 'aggregation.expected_distribution',
    };
  }
  if (preData?.expected_distribution && Object.keys(preData.expected_distribution).length > 0) {
    return {
      distribution: preData.expected_distribution,
      percentages: preData.expected_distribution, // pre-analytics already returns percentages
      mode: 'uncertainty_aware',
      source: 'preData.expected_distribution',
    };
  }
  if (aggregation?.percentages) {
    return {
      distribution: aggregation.distribution || null,
      percentages: aggregation.percentages,
      mode: 'sampled',
      source: 'aggregation.distribution',
    };
  }
  return { distribution: null, percentages: null, mode: null, source: null };
}

// Convenience: detect if a simulation/run has uncertainty-aware data available.
export function hasUncertaintyAwareData({ aggregation, preData } = {}) {
  return !!(
    (aggregation?.expected_percentages && Object.keys(aggregation.expected_percentages).length > 0) ||
    (preData?.expected_distribution && Object.keys(preData.expected_distribution).length > 0)
  );
}
